import { Injectable } from '@angular/core';
import { OcrPoint } from './ocr.model';

const CROP_QUALITY = 0.93;

interface Bounds {
  minX: number;
  minY: number;
  width: number;
  height: number;
}

function boundsOf(points: OcrPoint[], width: number, height: number): Bounds {
  const corners = points.slice(1).map(point => ({ x: point.X * width, y: point.Y * height }));
  const minX = Math.min(...corners.map(corner => corner.x));
  const minY = Math.min(...corners.map(corner => corner.y));
  return {
    minX,
    minY,
    width: Math.max(...corners.map(corner => corner.x)) - minX,
    height: Math.max(...corners.map(corner => corner.y)) - minY
  };
}

function clipToPolygon(image: HTMLImageElement, points: OcrPoint[]): string {
  const source = document.createElement('canvas');
  source.width = image.width;
  source.height = image.height;
  const sourceContext = source.getContext('2d');
  if (sourceContext) {
    sourceContext.beginPath();
    sourceContext.moveTo(points[0].X * image.width, points[0].Y * image.height);
    points.slice(1).forEach(point => sourceContext.lineTo(point.X * image.width, point.Y * image.height));
    sourceContext.closePath();
    sourceContext.clip();
    sourceContext.drawImage(image, 0, 0);
  }

  const bounds = boundsOf(points, image.width, image.height);
  const cropped = document.createElement('canvas');
  cropped.width = bounds.width;
  cropped.height = bounds.height;
  cropped
    .getContext('2d')
    ?.drawImage(source, bounds.minX, bounds.minY, bounds.width, bounds.height, 0, 0, bounds.width, bounds.height);
  return cropped.toDataURL('image/jpeg', CROP_QUALITY);
}

@Injectable({ providedIn: 'root' })
export class OcrImageCropper {
  public crop(base64Image: string, polygon: OcrPoint[]): Promise<string> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(clipToPolygon(image, polygon));
      image.onerror = () => reject(new Error('Could not decode image'));
      image.src = base64Image;
    });
  }
}
